/**
 * AppHomePage Component
 * 
 * Main dashboard/homepage for authenticated users.
 * Features NextMoveCard, My Clubs, and horizontal event scroll.
 */

import { useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import TopBar from '../components/TopBar';
import NextMoveCard from '../components/NextMoveCard';
import EventCard from '../components/EventCard';
import ClubCard from '../components/ClubCard';
import { useAllEvents } from '../hooks/useEvents';
import { useClubs } from '../hooks/useClubs';
import { Button, EventCardSkeleton, ClubCardSkeleton } from '../components';
import { EmptyEventsFallback } from '../components/EmptyEventsFallback';
import { createHomeMobileMenu } from '../components/MobileContextMenu';

export default function AppHomePage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [currentTime] = useState(() => Math.floor(Date.now() / 1000));
  
  // Create mobile menu configuration
  const homeMobileMenu = createHomeMobileMenu(navigate, logout);
  
  // Fetch user's events and clubs
  const { data: events = [], isLoading: eventsLoading } = useAllEvents();
  const { data: clubs = [], isLoading: clubsLoading } = useClubs();

  // Generate stable mock data for development (TODO: Replace with real API data)
  const mockClubData = useMemo(() => {
    return clubs.map((club, index) => ({
      unread_announcements: index % 3, // Stable mock data
      upcoming_events_this_week: club.upcoming_event_count || (index % 4),
      recent_activity: index % 4 === 0, // Every 4th club has recent activity
    }));
  }, [clubs]);

  // Filter upcoming events (next 30 days for better discovery)
  const upcomingEvents = events
    .filter(event => event.starts_at > currentTime)
    .sort((a, b) => a.starts_at - b.starts_at) // Chronological order (earliest first)
    .slice(0, 15); // Show first 15 events for better discovery

  // Get next RSVP'd event for NextMoveCard (only show events user has RSVP'd to)
  const rsvpdEvents = events
    .filter(event => 
      event.starts_at > currentTime && 
      (event.rsvp_status === 'yes' || event.rsvp_status === 'maybe')
    )
    .sort((a, b) => a.starts_at - b.starts_at);
  
  const nextEvent = rsvpdEvents[0];

  return (
    <>
      <TopBar title="Home" mobileMenu={homeMobileMenu} />
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-6 space-y-8">
          {/* Hero - Next Move */}
          {nextEvent ? (
            <section>
              <NextMoveCard
                title={nextEvent.title}
                subtitle={`${nextEvent.club_name || 'Event'} • ${new Date(nextEvent.starts_at * 1000).toLocaleDateString('en-US', { 
                  weekday: 'long',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}`}
                event={{
                  ...nextEvent,
                  startTime: new Date(nextEvent.starts_at * 1000),
                  userRsvpStatus: nextEvent.rsvp_status,
                  rsvpCount: nextEvent.rsvp_count || 0,
                  distanceKm: nextEvent.distance_km,
                  registrationStatus: 'open' as const // TODO: Add real registration status from API
                }}
                primaryAction={{
                  label: 'View Event',
                  onClick: () => navigate(`/event/${nextEvent.id}`)
                }}
                secondaryAction={{
                  label: 'View Club',
                  onClick: () => navigate(`/clubs/${nextEvent.club_slug}`)
                }}
                backgroundImage={nextEvent.hero_image_url || undefined} // Backend COALESCE provides fallback
                className="mb-2"
              />
            </section>
          ) : (
            <section>
              <NextMoveCard
                title={upcomingEvents.length > 0 ? "Your Next Event" : "Welcome back!"}
                subtitle={upcomingEvents.length > 0 
                  ? "RSVP to events to see your next move here"
                  : "No upcoming events yet. Explore clubs to find activities."
                }
                primaryAction={{
                  label: upcomingEvents.length > 0 ? 'Browse Events' : 'Browse Clubs',
                  onClick: () => navigate(upcomingEvents.length > 0 ? '/events' : '/clubs')
                }}
                secondaryAction={{
                  label: upcomingEvents.length > 0 ? 'View Clubs' : 'Create Event', 
                  onClick: () => navigate(upcomingEvents.length > 0 ? '/clubs' : '/events/create')
                }}
                className="mb-2"
              />
            </section>
          )}

          {/* My Clubs */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-text-primary">My Clubs</h2>
              <button 
                className="text-accent hover:text-accent-light transition-colors text-xs"
                onClick={() => navigate('/clubs')}
              >
                Browse All
              </button>
            </div>
            
            {clubsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <ClubCardSkeleton key={i} />
                ))}
              </div>
            ) : clubs.length > 0 ? (
              <div className="space-y-3">
                {clubs
                  .sort((a, b) => {
                    // Smart sorting: notifications first, then upcoming events, then alphabetical
                    const aExtended = a as typeof a & { unread_announcements?: number; upcoming_events_this_week?: number };
                    const bExtended = b as typeof b & { unread_announcements?: number; upcoming_events_this_week?: number };
                    const aHasNotifications = (aExtended.unread_announcements || 0) > 0;
                    const bHasNotifications = (bExtended.unread_announcements || 0) > 0;
                    const aHasEvents = (aExtended.upcoming_events_this_week || 0) > 0;
                    const bHasEvents = (bExtended.upcoming_events_this_week || 0) > 0;
                    
                    if (aHasNotifications !== bHasNotifications) {
                      return bHasNotifications ? 1 : -1;
                    }
                    if (aHasEvents !== bHasEvents) {
                      return bHasEvents ? 1 : -1;
                    }
                    return a.name.localeCompare(b.name);
                  })
                  .map((club, index) => {
                    const mockData = mockClubData[index] || { unread_announcements: 0, upcoming_events_this_week: 0, recent_activity: false };
                    return (
                      <ClubCard
                        key={club.id}
                        club={{
                          ...club,
                          // Mock notification data - replace with real API data
                          unread_announcements: mockData.unread_announcements,
                          upcoming_events_this_week: mockData.upcoming_events_this_week,
                          recent_activity: mockData.recent_activity,
                          avatar_url: club.hero_image_url || undefined
                        }}
                        onClick={() => navigate(`/clubs/${club.slug}`)}
                      />
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-text-muted mb-4">You haven't joined any clubs yet</p>
                <Button
                  variant="primary"
                  onClick={() => navigate('/clubs')}
                >
                  Find Clubs
                </Button>
              </div>
            )}
          </section>

          {/* Upcoming Events - Club Events (Expanded) */}
          <section className="pb-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-text-primary">Upcoming Events</h2>
              <button 
                className="text-accent hover:text-accent-light transition-colors text-xs"
                onClick={() => navigate('/events')}
              >
                View All
              </button>
            </div>
            
            {eventsLoading ? (
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                {[1, 2, 3, 4].map((i) => (
                  <EventCardSkeleton key={i} />
                ))}
              </div>
            ) : upcomingEvents.length > 0 ? (
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                {upcomingEvents.map((event) => (
                  <EventCard
                    key={event.id}
                    event={{
                      id: event.id,
                      title: event.title,
                      date: new Date(event.starts_at * 1000).toLocaleDateString('en-US', { 
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      }),
                      starts_at: event.starts_at,
                      ends_at: event.ends_at || undefined,
                      location: event.location_name || event.location_address || 'TBD',
                      attendees: event.rsvp_count || 0,
                      imageUrl: event.hero_image_url || '', // Backend COALESCE handles event → club fallback
                      distance_km: event.distance_km || undefined,
                      rsvp_count: event.rsvp_count || 0,
                      user_rsvp_status: event.rsvp_status || null
                    }}
                    onClick={() => navigate(`/event/${event.id}`)}
                  />
                ))}
              </div>
            ) : (
              <EmptyEventsFallback variant="be-the-first" />
            )}
          </section>
        </div>
      </div>
    </>
  );
}